import { ChangeDetectionStrategy, Component, HostListener, OnDestroy, OnInit, computed, effect, inject, signal, untracked } from '@angular/core';
import { MONEY_CODE_MODULE_PERMISSIONS, REPORT_MODULE_PERMISSIONS } from '../../../constants/permissions';
import { Router, RouterModule } from '@angular/router';

import { SvgIconsComponent } from '../svg-icons/svg-icons.component';
import { FullpageLoadingComponent } from '../fullpage-loading/fullpage-loading.component';
import { localRect, localViewport } from '../../commonFunction/app-zoom.util';
import { NavV2Service } from './nav-v2.service';
import { Helpers } from '../../utils/helpers';
import { AuthApis } from '../../../services/authApis';
import { AppConfigService } from '../../../appConfigService/config';
import { FloatingWindowService } from '../floating-window/floating-window.service';
import { Store } from '@ngrx/store';
import { toSignal } from '@angular/core/rxjs-interop';
import { Observable } from 'rxjs';
import { selectUserDetail } from '../../store/user/user.selector';
import { ThemeService } from '../../../services/theme/theme.service';
import { ClockWidgetService } from '../usa-timezone-clock/clock-widget.service';
import { Services } from '../../../services/service';
import { StatsApis } from '../../../services/statsApis';
import { MyRemindersService } from '../my-reminders/my-reminders.service';
import { VERSION } from '../../../../version-status';
import { ContextMenuService } from '../context-menu/context-menu.service';
import { ContextMenuItem } from '../context-menu/context-menu.component';

export interface NavV2Leaf {
  label: string;
  url: string;
  icon: string;
  permission?: string | string[];
}

export interface NavV2Item {
  id: string;
  label: string;
  icon: string;
  permission?: string | string[];
  url?: string;
  action?: 'FLOATING_WINDOW' | 'REMINDERS_POPUP';
  actionPayload?: string;
  children?: NavV2Leaf[];
}

export interface NavV2Section {
  label: string;
  items: NavV2Item[];
}

const NAV_SECTIONS: NavV2Section[] = [
  {
    label: 'Tasks',
    items: [
      { id: 'my-reminders', label: 'My Reminders', icon: 'notification', action: 'REMINDERS_POPUP' },
      { id: 'my-tasks', label: 'My Tasks', icon: 'task-alt', action: 'FLOATING_WINDOW', actionPayload: 'workspace' },
    ],
  },
  {
    label: 'Main',
    items: [
      { id: 'dashboard', label: 'Dashboard', icon: 'dashboard', url: '/' },
      {
        id: 'dispatch',
        label: 'Dispatch',
        icon: 'dispatch',
        permission: 'DIS101',
        children: [
          // { label: 'Active Loads (Old View)', url: 'dispatch/loads/running', icon: 'package', permission: 'DIS102' },
          { label: 'Active Loads', url: 'dispatch/loads/running-v2', icon: 'deployed-code', permission: 'DIS102' },
          { label: 'Journey Board', url: 'dispatch/running', icon: 'local-shipping', permission: 'DIS103' },
          { label: 'Delivered Loads', url: 'dispatch/loads/completed', icon: 'orders', permission: 'DIS107' },
          { label: 'Cancelled Load', url: 'dispatch/loads/cancelled', icon: 'box-cancel' },
          { label: 'Detentions', url: 'dispatch/detention', icon: 'hourglass', permission: 'DIS114' },
          { label: 'Driver Trips', url: 'dispatch/trip', icon: 'signpost', permission: 'DIS119' },
          { label: 'Reefer Reporting', url: 'dispatch/reefer-reporting', icon: 'ac-unit', permission: 'DIS122' },
          { label: 'Load Templates', url: 'dispatch/load-templates', icon: 'description', permission: 'DIS125' },
          { label: 'Load Movements', url: 'dispatch/load-movements', icon: 'route' },
        ],
      },
      {
        id: 'planning',
        label: 'Planning',
        icon: 'edit-calendar',
        children: [{ label: 'Route Map', url: 'route-map', icon: 'map', permission: 'ADMIN' }],
      },
      {
        id: 'accounting',
        label: 'Accounting',
        icon: 'accounting',
        permission: 'ACC101',
        children: [
          { label: 'Chart of Accounts', url: '/accounting/chart-of-accounts', icon: 'account-tree', permission: 'ACC109' },
          // { label: 'All Transactions', url: '/accounting/transactions', icon: 'sync-alt', permission: 'ADMIN' },
          // { label: 'Acc Transactions', url: '/accounting/account-transactions', icon: 'receipt-long', permission: 'ADMIN' },
          { label: 'Driver Settlements', url: '/accounting/driver-settlements', icon: 'handshake-m', permission: 'ACC117' },
          { label: 'Load Invoices', url: '/billing/ready-to-bill', icon: 'request-quote', permission: 'ACC124' },
          { label: 'Batches', url: '/billing/batches', icon: 'stacks', permission: 'ACC130' },
          { label: 'Accounts Overview', url: '/accounting/accounts-overview', icon: 'accounting', permission: 'ACC145' },
        ],
      },
      {
        id: 'money-code',
        label: 'Money Code',
        icon: 'currency',
        permission: 'MNY101',
        children: [
          { label: 'List', url: '/money-code', icon: 'list-alt', permission: MONEY_CODE_MODULE_PERMISSIONS },
          { label: 'Providers', url: 'money-code/providers', icon: 'storefront', permission: 'ADMIN' },
        ],
      },
      {
        id: 'assets',
        label: 'Assets',
        icon: 'assets',
        permission: 'AST101',
        children: [
          { label: 'Drivers', url: '/assets/driver', icon: 'driver', permission: 'AST102' },
          { label: 'Driver Off', url: '/assets/driver-offs', icon: 'offs', permission: 'DRV103' },
          { label: 'Driver Requests', url: '/assets/driver-requests', icon: 'notes', permission: 'DRV103' },
          { label: 'Truck', url: '/assets/truck', icon: 'truck', permission: 'AST107' },
          { label: 'Trailer', url: '/assets/trailer', icon: 'trailer', permission: 'AST112' },
          { label: 'Continuity', url: '/assets/continuity', icon: 'conversion-path' },
          { label: 'Companies', url: '/assets/companies', icon: 'company', permission: 'ADMIN' },
          { label: 'Compliance Records', url: '/assets/compliance-records', icon: 'handshake', permission: 'ADMIN' },
          { label: 'Location Tracking', url: '/assets/location-tracking', icon: 'share-location' },
          { label: 'Documents', url: '/assets/documents', icon: 'topic' },
          { label: 'Asset Makers', url: '/assets/asset-makers', icon: 'maker1', permission: 'ADMIN' },
          { label: 'Asset Models', url: '/assets/asset-models', icon: 'model', permission: 'ADMIN' },
        ],
      },
      { id: 'customers', label: 'Customers', icon: 'customer1', url: '/customer', permission: 'CUS103' },
      {
        id: 'locations',
        label: 'Locations',
        icon: 'locationList',
        permission: 'LOC101',
        children: [{ label: 'Address Book', url: '/locations/address-book', icon: 'address-book1', permission: 'LOC122' }],
      },
      { id: 'reports', label: 'Reports', icon: 'monitoring', url: '/reports', permission: REPORT_MODULE_PERMISSIONS },
      { id: 'background-audits', label: 'Background Audits', icon: 'policy', url: '/background-audits', permission: 'ADMIN' },
      { id: 'background-process', label: 'Background Process', icon: 'sync', url: '/background-process', permission: 'ADMIN' },
      {
        id: 'settings',
        label: 'Settings',
        icon: 'setting',
        children: [
          { label: 'Accounting', url: '/settings/accounting', icon: 'accounting', permission: 'ADMIN' },
          { label: 'Change Password', url: '/settings/change-password', icon: 'lock' },
          { label: 'Checklist', url: '/settings/checklist', icon: 'checklist-m', permission: 'ADMIN' },
          { label: 'Communication', url: '/settings/communication', icon: 'mail', permission: 'ADMIN' },
          { label: 'Document Type', url: '/settings/document-type', icon: 'description', permission: 'ADMIN' },
          { label: 'Driver Pay Rate', url: '/settings/driver-pay-rate', icon: 'driver-pay1', permission: 'ADMIN' },
          { label: 'Empty Miles Type', url: '/settings/empty-miles-type', icon: 'tracking2', permission: 'ADMIN' },
          { label: 'General Settings', url: '/settings/general-settings', icon: 'setting', permission: 'ADMIN' },
          { label: 'Integration', url: '/settings/integration', icon: 'share', permission: 'ADMIN' },
          { label: 'Labels', url: '/settings/labels', icon: 'label-m', permission: 'ADMIN' },
          { label: 'Load Blocker Type', url: '/settings/load-blocker-type', icon: 'block', permission: 'ADMIN' },
          { label: 'Options', url: '/settings/options', icon: 'tune', permission: 'ADMIN' },
          { label: 'Payment Methods', url: '/settings/payment-methods', icon: 'credit-card', permission: 'ADMIN' },
          { label: 'Reasons', url: '/settings/reasons', icon: 'info', permission: 'ADMIN' },
          { label: 'Task Recurrences', url: '/settings/task-recurrence', icon: 'refresh', permission: 'ADMIN' },
          { label: 'Teams', url: '/settings/teams', icon: 'team', permission: 'ADMIN' },
          { label: 'Terminals', url: '/settings/terminal', icon: 'garage', permission: 'ADMIN' },
          { label: 'User Management', url: '/settings/user-management', icon: 'manage-accounts', permission: 'ADMIN' },
        ],
      },
      {
        id: 'maintenance',
        label: 'Maintenance',
        icon: 'maintenance',
        children: [
          { label: 'Trucks', url: 'maintenance', icon: 'auto_towing' },
          { label: 'Trailers', url: 'maintenance/trailer', icon: 'rv-hookup' },
        ],
      },
      { id: 'ms-customers', label: 'MS Customers', icon: 'groups', url: '/customer-microservice', permission: 'ADMIN' },
    ],
  },
  {
    label: 'More',
    items: [
      { id: 'sessions', label: 'Sessions', icon: 'fingerprint', url: '/sessions', permission: 'ADMIN' },
      {
        id: 'development',
        label: 'Development',
        icon: 'science',
        permission: 'ADMIN',
        children: [
          { label: 'Backfills', url: '/development/backfills', icon: 'history', permission: 'ADMIN' },
          { label: 'Broker Directory Stub', url: '/development/broker-directory-stub', icon: 'query-stats', permission: 'ADMIN' },
          { label: 'Broker Linking', url: '/development/broker-link', icon: 'link-off', permission: 'ADMIN' },
          { label: 'Development', url: '/development/content', icon: 'terminal', permission: 'ADMIN' },
          { label: 'Incomes & Expenses', url: '/development/temp-get-all-incomes-expenses-of-loads', icon: 'query-stats', permission: 'ADMIN' },
          { label: 'Files Chunks Upload', url: '/development/file-chunks-upload', icon: 'upload-file', permission: 'ADMIN' },
          { label: 'Not Linked to Stop Expenses and Load', url: '/development/not-linked-to-stop-expenses-and-load', icon: 'link-off', permission: 'ADMIN' },
          { label: 'Buttons Gallery', url: '/development/buttons-gallery', icon: 'palette', permission: 'ADMIN' },
        ],
      },
    ],
  },
];

interface FlyoutState {
  item: NavV2Item;
  top: number;
  maxHeight: number;
}
interface PinnedEntry {
  key: string;
  label: string;
  icon: string;
  url?: string;
  action?: 'FLOATING_WINDOW' | 'REMINDERS_POPUP';
  actionPayload?: string;
  origin: string;
}

@Component({
  selector: 'app-left-side-bar-v2',
  standalone: true,
  imports: [RouterModule, SvgIconsComponent, FullpageLoadingComponent],
  templateUrl: './left-side-bar-v2.component.html',
  styleUrl: './left-side-bar-v2.component.css',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class LeftSideBarV2Component implements OnInit, OnDestroy {
  nav = inject(NavV2Service);
  themeService = inject(ThemeService);
  readonly clockWidget = inject(ClockWidgetService);
  private helper = inject(Helpers);
  private auth = inject(AuthApis);
  private router = inject(Router);
  private appConfig = inject(AppConfigService);
  private store = inject(Store);
  private floatingWindow = inject(FloatingWindowService);
  private contextMenu = inject(ContextMenuService);
  private remindersUi = inject(MyRemindersService);
  private api = inject(Services);
  private statsApis = inject(StatsApis);
  readonly feVersion = VERSION;
  beVersion = signal<string>('');

  sections = signal<NavV2Section[]>([]);
  logo = signal<string>('');
  user = toSignal(this.store.select(selectUserDetail) as Observable<any>, { initialValue: null });
  initials = computed(() => {
    const name = (this.user() as any)?.name?.trim();
    if (!name) return '';
    const parts = name.split(' ').filter(Boolean);
    const first = parts[0]?.[0] ?? '';
    const last = parts.length > 1 ? (parts[parts.length - 1]?.[0] ?? '') : '';
    return (first + last).toUpperCase();
  });
  loading = false;

  flyout = signal<FlyoutState | null>(null);
  profileOpen = signal(false);

  myTasksCount = signal<number | null>(null);
  myTasksBadge = computed(() => {
    const count = this.myTasksCount();
    if (count == null || count <= 0) return '';
    return count > 99 ? '99+' : String(count);
  });
  myRemindersBadge = computed(() => {
    const count = this.remindersUi.dueCount();
    if (!count || count <= 0) return '';
    return count > 99 ? '99+' : String(count);
  });
  private tasksCountTimer: any = null;
  private onWindowFocus = () => this.fetchMyTasksCount();

  private fetchMyTasksCount() {
    this.statsApis.getTasksCounts().subscribe({
      next: (res: any) => {
        const c = res?.response?.detail?.tasks_count;
        if (!c) return;
        const n = (v: any) => (Number.isFinite(Number(v)) ? Number(v) : null);
        this.myTasksCount.set(n(c.user_tasks) ?? n(c.user_assigned));
      },
      error: () => {},
    });
  }
  private leafIndex = computed(() => {
    const map = new Map<string, PinnedEntry>();
    for (const section of this.sections()) {
      for (const item of section.items) {
        if (item.children) {
          for (const c of item.children) {
            map.set(c.url, { key: c.url, label: c.label, icon: c.icon, url: c.url, origin: item.label });
          }
        } else {
          const key = item.url ?? item.id;
          map.set(key, { key, label: item.label, icon: item.icon, url: item.url, action: item.action, actionPayload: item.actionPayload, origin: section.label });
        }
      }
    }
    return map;
  });
  pinnedItems = computed(() => {
    const index = this.leafIndex();
    return this.nav
      .pinned()
      .map((key) => index.get(key))
      .filter((entry): entry is PinnedEntry => !!entry);
  });
  activeParentId = computed(() => {
    const url = this.nav.activeUrl();
    for (const section of this.sections()) {
      for (const item of section.items) {
        if (item.children?.some((c) => this.nav.isRouteActive(c.url, url))) return item.id;
      }
    }
    return null;
  });

  constructor() {
    // Reveal the section that owns the active route (add-only: never closes
    // sections the user opened — fixes the legacy collapse-on-navigate jump).
    effect(() => {
      const id = this.activeParentId();
      if (id) untracked(() => this.nav.openSection(id));
    });
    // Navigation closes any open overlay.
    effect(() => {
      this.nav.activeUrl();
      this.flyout.set(null);
      this.profileOpen.set(false);
    });
  }

  ngOnInit(): void {
    // Permissions are session-static: filter once.
    this.sections.set(
      NAV_SECTIONS.map((section) => ({
        label: section.label,
        items: section.items
          .filter((item) => this.helper.handleCheckPermissions(item.permission ?? ''))
          .map((item) => ({
            ...item,
            children: item.children?.filter((c) => this.helper.handleCheckPermissions(c.permission ?? '')),
          }))
          .filter((item) => !item.children || item.children.length > 0),
      })).filter((section) => section.items.length > 0)
    );

    this.logo.set(this.appConfig.AppConfigData?.logo ?? '');

    // GET /api/status returns AppStatus verbatim: { status: 'Running', version: 'x.y.z' }
    this.api.getRequest<any>('status').subscribe({
      next: (res: any) => {
        const v = res?.version ?? res?.response?.version ?? res?.response?.detail?.version;
        if (v) this.beVersion.set(String(v));
      },
      error: () => {},
    });

    try {
      if (sessionStorage.getItem('open-tasks-after-login') === '1') {
        sessionStorage.removeItem('open-tasks-after-login');
        setTimeout(() => this.floatingWindow.open('workspace'), 400);
      }
    } catch {}

    if (typeof window !== 'undefined') {
      this.fetchMyTasksCount();
      this.tasksCountTimer = setInterval(() => this.fetchMyTasksCount(), 120000);
      window.addEventListener('focus', this.onWindowFocus);
    }
  }

  ngOnDestroy(): void {
    clearInterval(this.tasksCountTimer);
    clearTimeout(this.myTasksClickTimer);
    if (typeof window !== 'undefined') window.removeEventListener('focus', this.onWindowFocus);
  }

  private allLeafUrls = computed(() => {
    const urls: string[] = [];
    for (const section of this.sections()) {
      for (const item of section.items) {
        if (item.children) {
          for (const c of item.children) if (c.url) urls.push(c.url);
        } else if (item.url) {
          urls.push(item.url);
        }
      }
    }
    return urls;
  });

  isActive(url?: string): boolean {
    if (!url) return false;
    const current = this.nav.activeUrl();
    if (!this.nav.isRouteActive(url, current)) return false;
    const myLength = (url.startsWith('/') ? url : '/' + url).length;
    return !this.allLeafUrls().some((other) => {
      if (other === url) return false;
      const otherLength = (other.startsWith('/') ? other : '/' + other).length;
      return otherLength > myLength && this.nav.isRouteActive(other, current);
    });
  }

  onParentClick(item: NavV2Item, event: MouseEvent): void {
    if (this.nav.collapsed()) {
      if (this.flyout()?.item.id === item.id) {
        this.flyout.set(null);
        return;
      }
      // local (pre-zoom) px space — the fixed flyout's top px is re-zoomed at render
      const rect = localRect(event.currentTarget as HTMLElement);
      // keep the flyout inside the band above the alerts marquee (--app-bottom-bar)
      const gap = 12;
      const limit = localViewport().height - this.bottomBarHeight() - gap;
      const estHeight = 44 + (item.children?.length ?? 0) * 30 + 12;
      const top = Math.max(8, Math.min(rect.top, limit - estHeight));
      const maxHeight = limit - top;
      this.flyout.set({ item, top, maxHeight });
    } else {
      this.nav.toggleSection(item.id);
    }
  }
  private bottomBarHeight(): number {
    const raw = getComputedStyle(document.documentElement).getPropertyValue('--app-bottom-bar');
    const value = parseFloat(raw);
    if (!value) return 0;
    if (raw.includes('rem')) {
      const rootFont = parseFloat(getComputedStyle(document.documentElement).fontSize) || 16;
      return value * rootFont;
    }
    return value;
  }

  onAction(item: { id?: string; key?: string; action?: 'FLOATING_WINDOW' | 'REMINDERS_POPUP'; actionPayload?: string }): void {
    if (item.action === 'REMINDERS_POPUP') {
      this.remindersUi.open.set(true);
      return;
    }
    if (item.action === 'FLOATING_WINDOW' && item.actionPayload) {
      const params = (item.id ?? item.key) === 'my-tasks' ? { mine: '1' } : undefined;
      this.floatingWindow.open(item.actionPayload, params);
    }
  }

  togglePin(key: string, event: Event): void {
    event.preventDefault();
    event.stopPropagation();
    this.nav.togglePin(key);
  }

  pipSupported = FloatingWindowService.pipSupported();

  pinMyTasks(event: Event): void {
    event.preventDefault();
    event.stopPropagation();
    this.floatingWindow.openPinnedFromMain({ mine: '1' });
  }

  private myTasksClickTimer: any = null;

  myTasksMenu: ContextMenuItem[] = [
    { title: 'Open in normal mode', icon: 'fullscreen', action: () => this.openTasksNormal() },
    { title: 'Open in Pinned mode', icon: 'pip', action: () => this.openTasksPinned(), hidden: !FloatingWindowService.pipSupported() },
  ];

  openTasksNormal(): void {
    this.floatingWindow.open('workspace', { mine: '1' });
  }

  openTasksPinned(): void {
    const current = this.floatingWindow.getFloatingParams('workspace');
    if (!current) {
      this.floatingWindow.openPinnedFromMain({ mine: '1' });
      return;
    }
    const carried: Record<string, string> = {};
    for (const key of Object.keys(current)) {
      if (key !== 'panel' && key !== 'pinned') carried[key] = current[key];
    }
    this.floatingWindow.openPinnedFromMain(carried);
  }

  onActionItemClick(item: NavV2Item, event: MouseEvent): void {
    if (item.id === 'my-tasks') {
      const anchor = event.currentTarget as HTMLElement;
      clearTimeout(this.myTasksClickTimer);
      this.myTasksClickTimer = setTimeout(() => this.contextMenu.open(anchor, this.myTasksMenu), 250);
      return;
    }
    this.onAction(item);
  }

  onActionItemDblClick(item: NavV2Item): void {
    if (item.id !== 'my-tasks') return;
    clearTimeout(this.myTasksClickTimer);
    this.openTasksPinned();
  }

  closeFlyout(): void {
    this.flyout.set(null);
  }

  toggleProfile(event: Event): void {
    event.stopPropagation();
    this.profileOpen.update((v) => !v);
  }

  async handleLogout(): Promise<void> {
    this.loading = true;
    try {
      await this.auth.logout();
      this.router.navigate(['/auth/login'], { replaceUrl: true });
    } finally {
      this.loading = false;
    }
  }

  themeTitle(): string {
    const t = this.themeService.theme();
    return t === 'light' ? 'Switch to soft mode' : t === 'soft' ? 'Switch to dark mode' : 'Switch to light mode';
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent): void {
    const target = event.target as HTMLElement;
    if (this.flyout() && !target.closest('.nav2-flyout') && !target.closest('.nav2-parent')) {
      this.flyout.set(null);
    }
    if (this.profileOpen() && !target.closest('.nav2-profile-card') && !target.closest('.nav2-avatar')) {
      this.profileOpen.set(false);
    }
  }

  @HostListener('document:keydown.escape')
  onEscape(): void {
    this.flyout.set(null);
    this.profileOpen.set(false);
  }

  @HostListener('window:resize')
  onWindowResize(): void {
    this.flyout.set(null);
    this.profileOpen.set(false);
  }
}
